<template>
  <div class="space-y-6">
    <div v-if="toast" role="alert" class="rounded-md bg-red-tint px-4 py-3 text-sm text-gray-900">{{ toast }}</div>
    <div class="bg-white rounded-lg border border-gray-200">
      <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
        <h2 class="text-sm font-semibold text-gray-900">Schedules</h2>
        <span class="text-xs text-gray-500">{{ shown.length }}</span>
        <div class="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs">
          <button v-for="f in ['all', 'active', 'paused']" :key="f" type="button" :class="['px-3 py-1', filter === f ? 'bg-neutral-100 text-gray-900' : 'bg-white text-gray-600']" @click="filter = f">{{ words(f) }}</button>
        </div>
        <router-link :to="{ name: 'AdminHermesSchedule', params: { scheduleId: 'new' } }" :class="[PRIMARY_BUTTON, 'ml-auto', error ? 'pointer-events-none opacity-50' : '']">New schedule</router-link>
      </div>
      <div v-if="error" class="m-4 rounded-md bg-red-tint px-4 py-3 text-sm text-gray-900"><strong>Schedules could not be loaded.</strong> {{ error }} <button type="button" :class="[TEXT_BUTTON, 'ml-3']" @click="load">Retry</button></div>
      <table v-else class="w-full table-fixed divide-y divide-gray-200">
        <colgroup><col style="width:16%"><col style="width:13%"><col style="width:12%"><col style="width:9%"><col style="width:13%"><col style="width:20%"><col style="width:9%"><col style="width:8%"></colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH">Template</th><th :class="TH">Executor</th><th :class="TH">Params</th><th :class="TH">Cadence</th>
          <th :class="TH">Next due</th><th :class="TH">Last run</th><th :class="TH">Active</th><th :class="[TH, 'text-right']">Actions</th>
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <template v-if="loading"><tr v-for="n in 3" :key="n"><td v-for="c in 8" :key="c" :class="TD"><div class="h-4 rounded bg-gray-100" /></td></tr></template>
          <tr v-else-if="!shown.length"><td colspan="8" class="px-3 py-8 text-center text-sm text-gray-500">{{ schedules.length ? 'No schedules match this filter.' : 'No schedules yet. Every run so far was started by a person or an event.' }}</td></tr>
          <template v-for="(s, i) in shown" v-else :key="s.id">
            <tr>
              <td :class="[TD, lastFailed(s) ? 'border-l-2 border-primary-500' : '']">
                <router-link :to="{ name: 'AdminHermesSchedule', params: { scheduleId: s.id } }" class="font-medium text-neutral-800">{{ s.template_name }}</router-link>
              </td>
              <td :class="TD">{{ s.executor_name }}<div class="text-xs text-gray-500 font-mono">{{ s.executor_key }}</div></td>
              <td :class="TD">
                <span v-for="(v, k) in s.params" :key="k" class="inline-block rounded bg-gray-100 px-2 mr-1 mb-1 text-xs font-mono">{{ k }} {{ v }}</span>
                <span v-if="!Object.keys(s.params || {}).length" class="inline-block rounded bg-gray-100 px-2 text-xs">Full population</span>
              </td>
              <td :class="TD">{{ words(s.cadence) }}<div class="text-xs text-gray-500">{{ intervalWords[s.cadence] }}</div></td>
              <td :class="TD" :title="nextTitle(s)">{{ nextLabel(s) }}</td>
              <td :class="TD">
                <template v-if="s.last_run_summary">
                  <HermesBadge :value="s.last_run_summary.status" />
                  <router-link :to="{ name: 'AdminHermesRun', params: { runId: s.last_run_summary.id } }" class="ml-2 text-neutral-800 font-mono text-xs">#{{ s.last_run_summary.id }}</router-link>
                  <div class="text-xs text-gray-500 mt-1">{{ money(s.last_run_summary.cost_usd) }} · {{ relative(s.last_run_summary.finished_at || s.last_run_summary.started_at) }}</div>
                </template>
                <span v-else class="text-gray-500">Never run</span>
              </td>
              <td :class="TD"><label class="inline-flex items-center gap-2 text-xs"><input type="checkbox" :checked="s.is_active" @change="toggle(s)"> {{ s.is_active ? 'Active' : 'Paused' }}</label></td>
              <td :class="[TD, 'text-right']"><router-link :to="{ name: 'AdminHermesSchedule', params: { scheduleId: s.id } }" :class="TEXT_BUTTON">Open</router-link></td>
            </tr>
            <tr v-if="s.legacy_sat_scheduled && (!shown[i + 1] || shown[i + 1].template !== s.template)">
              <td colspan="8" class="px-3 py-2 bg-neutral-50 text-xs text-neutral-900">
                Legacy SAT {{ s.legacy_sat }} still runs on its own schedule, so both it and the {{ s.template_name }} schedule above will run.
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import HermesBadge from './HermesBadge.vue';
import { API, PRIMARY_BUTTON, TD, TEXT_BUTTON, TH, absolute, errorOf, money, relative, words } from './hermes';

export default {
  name: 'HermesSchedules',
  components: { HermesBadge },
  data() {
    return {
      schedules: [], loading: true, error: '', toast: '', filter: 'all',
      intervalWords: { daily: 'Every 20 hours', weekly: 'Every 7 days', monthly: 'Every 30 days' },
      PRIMARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    shown() {
      return this.schedules.filter((s) => this.filter === 'all' || (this.filter === 'active' ? s.is_active : !s.is_active));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    money, relative, words,
    async load() {
      this.loading = true;
      this.error = '';
      try {
        this.schedules = (await this.$axios.get(`${API}/schedules/`)).data;
      } catch (e) {
        this.error = errorOf(e, 'Try again in a moment.');
      } finally {
        this.loading = false;
      }
    },
    lastFailed(s) {
      return s.last_run_summary && s.last_run_summary.status === 'failed';
    },
    nextLabel(s) {
      if (s.next_due === 'now') return s.is_active ? 'Due now' : 'Paused';
      if (s.next_due === 'after_current') return 'After the current run finishes';
      if (!s.is_active) return 'Paused';
      return new Date(s.next_due) <= new Date() ? 'Due now' : relative(s.next_due);
    },
    nextTitle(s) {
      return ['now', 'after_current'].includes(s.next_due) ? '' : `${s.is_active ? '' : 'Would be '}${absolute(s.next_due)}`;
    },
    async toggle(s) {
      this.toast = '';
      const was = s.is_active;
      s.is_active = !was;
      try {
        await this.$axios.patch(`${API}/schedules/${s.id}/`, { is_active: s.is_active });
      } catch (e) {
        s.is_active = was;
        this.toast = errorOf(e, 'The change was not saved.');
      }
    },
  },
};
</script>
